import Router from 'next/router';
import { values } from 'ramda';
import { IGroup } from '@bridebook/models/source/models/Countries/Groups.types';
import { ITask as ISeedTask } from '@bridebook/models/source/models/Countries/Tasks.types';
import { ChecklistActionTypes } from '@bridebook/web/lib/checklist/action-types';
import {
  IChecklistFilter,
  IChecklistGroup,
  type TSubtaskUpdateMethod,
} from '@bridebook/web/lib/checklist/types';
import { openedSubtasksViewAnalytics } from '@bridebook/web/lib/checklist/analytics/actions';
import { extractId } from '@bridebook/web/lib/checklist/utils';
import { IDeps, type TTaskSerialized } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { ChecklistAnalyticsActionTypes } from '@bridebook/web/lib/checklist/analytics/action-types';

export const closeSubtasksView =
  (onBack = false) =>
  ({ getState }: IDeps) => {
    const {
      app: { previousPath },
    } = getState();

    if (onBack && previousPath) {
      Router.back();
    } else {
      Router.push(UrlHelper.checklist.main, undefined, { scroll: false });
    }

    return {
      type: ChecklistActionTypes.CLOSE_SUBTASKS,
    };
  };

export const clearSubtask = () => ({
  type: ChecklistActionTypes.CLOSE_SUBTASKS,
});

export const updateTaskSuccess = ({
  task,
  checked,
}: {
  task: TTaskSerialized;
  checked: boolean;
}) => ({
  type: ChecklistActionTypes.UPDATE_TASK_SUCCESS,
  payload: { task, checked },
});

export const updatedChecklistTotals = ({ done, total }: { done: number; total: number }) => ({
  type: ChecklistActionTypes.UPDATED_CHECKLIST_TOTALS,
  payload: { done, total },
});

export const fetchTasksInitialSuccess = (
  payload: Record<string, TTaskSerialized & Required<Pick<ISeedTask, 'i18nKey'>>>,
) => ({
  type: ChecklistActionTypes.FETCH_TASKS_INITIAL_SUCCESS,
  payload,
});

export const fetchUserChecklistSuccess = (payload: {
  result: Record<string, TTaskSerialized>;
  source: string;
  initializedAt?: number;
}) => ({
  type: ChecklistActionTypes.FETCH_USER_CHECKLIST_SUCCESS,
  payload,
});

export const showSubtasks =
  (id: string, server: boolean) =>
  ({ dispatch }: IDeps) => {
    const taskId: string = extractId(id) || '';
    const dispatchToggleDrawer = () => {
      dispatch(openedSubtasksViewAnalytics({}));
    };
    if (taskId) {
      if (server) {
        dispatchToggleDrawer();
      } else {
        // setTimeout required for smooth drawer animation
        setTimeout(() => dispatchToggleDrawer(), 150);
      }
    }

    return {
      type: ChecklistActionTypes.SHOW_SUBTASKS,
      payload: taskId,
    };
  };

export const updateGroup = (
  task: IGroup,
  checked: boolean,
  taskName = '',
  updatedMainTasksMethod = '',
) => ({
  type: ChecklistActionTypes.UPDATE_TASK_START,
  payload: {
    task,
    checked,
    taskName,
    updatedMainTasksMethod,
  },
});

// Ticks one checklist task by id; the epic validates the id against the
// store and runs the app's own subtask update (analytics included).
export const setTaskDoneById = (payload: { taskId: string; done: boolean }) => ({
  // `as const` keeps the exact member in the return type — the AI raw-dispatch
  // lexicon validates its `type` against it.
  type: ChecklistActionTypes.SET_TASK_DONE_BY_ID as const,
  payload,
});

export const updateSubtasks = (
  tasks: {
    taskId: string;
    checked: boolean;
    name?: string;
    method: TSubtaskUpdateMethod;
  }[],
) => ({
  type: ChecklistActionTypes.UPDATE_SUBTASKS as const,
  payload: { tasks },
});

export const updateSubtasksSuccess = (payload: { method: TSubtaskUpdateMethod }) => ({
  type: ChecklistActionTypes.UPDATE_SUBTASKS_SUCCESS,
  payload,
});

export const fetchRelatedSuppliers = (supplierSlug: string) => ({
  type: ChecklistActionTypes.FETCH_RELATED_SUPPLIERS,
  payload: { supplierSlug },
});

export const fetchRelatedSuppliersSuccess = (payload: any) => () => {
  const loadedSuppliers = values(payload.results);

  return {
    type: ChecklistActionTypes.FETCH_RELATED_SUPPLIERS_SUCCESS,
    payload: loadedSuppliers,
  };
};

export const initialiseChecklistAnalytics = (initial = true) => ({
  type: initial
    ? ChecklistAnalyticsActionTypes.INITIALISE_CHECKLIST_ANALYTICS
    : ChecklistAnalyticsActionTypes.REINITIALISE_CHECKLIST_ANALYTICS,
});

export const initialiseChecklist = (initial = true) => ({
  type: ChecklistActionTypes.INITIALISE_CHECKLIST,
  payload: { initial },
});

export const reInitialiseChecklist = (initial = true) => ({
  type: ChecklistActionTypes.REINITIALISE_CHECKLIST,
  payload: initial,
});

export const addTask = (payload: { name: string; period: string; groupId: string | null }) => ({
  type: ChecklistActionTypes.ADD_TASK as const,
  payload,
});

export const deleteTaskGroup = (payload: { groupId: string }) => ({
  type: ChecklistActionTypes.DELETE_TASK_GROUP as const,
  payload,
});

export const triggerDeleteTaskGroupAnalytics = (group: IGroup) => ({
  type: ChecklistAnalyticsActionTypes.TRIGGER_DELETE_TASK_MAIN_ANALYTICS,
  payload: {
    // todo: Fix payload property names and remove duplication
    name: group.name,
    task: group,
  },
});

export const deleteTask = (payload: { taskId: string }) => ({
  type: ChecklistActionTypes.DELETE_TASK as const,
  payload,
});

export const deleteCustomTaskById = (payload: { taskId: string }) => ({
  type: ChecklistActionTypes.DELETE_CUSTOM_TASK_BY_ID as const,
  payload,
});

/**
 * The result of a task write, which the AI planner waits for to report back.
 * The error says why nothing was written, so the planner can tell the couple.
 */
export const taskWriteSuccess = (payload: { taskId?: string } = {}) => ({
  type: ChecklistActionTypes.TASK_WRITE_SUCCESS as const,
  payload,
});

export const taskWriteError = (message: string) => ({
  type: ChecklistActionTypes.TASK_WRITE_ERROR as const,
  error: new Error(message),
});

// Renames a custom task by id. The epic leaves Bridebook (non-custom) tasks alone.
export const renameTaskById = (payload: { taskId: string; name: string }) => ({
  type: ChecklistActionTypes.RENAME_TASK_BY_ID as const,
  payload,
});

// Moves a custom task into a group, or out of its group as a main task when `groupId` is null.
export const setTaskGroupById = (payload: { taskId: string; groupId: string | null }) => ({
  type: ChecklistActionTypes.SET_TASK_GROUP_BY_ID as const,
  payload,
});

export const triggerDeleteTaskAnalytics = (
  task: IChecklistGroup,
  groupName: string,
  taskName: string,
) => ({
  type: ChecklistAnalyticsActionTypes.TRIGGER_DELETE_TASK_SUB_ANALYTICS,
  // todo: Fix payload property names
  payload: { task, taskName: groupName, name: taskName },
});

export const confirmDeleteSubtaskAnalytics = (task: TTaskSerialized | IGroup) => ({
  type: ChecklistAnalyticsActionTypes.DELETE_TASK_SUB_ANALYTICS,
  payload: { task },
});

export const confirmDeleteMainTaskAnalytics = (task: IChecklistGroup) => ({
  type: ChecklistAnalyticsActionTypes.DELETE_MAIN_TASK_ANALYTICS,
  payload: { task, taskName: task.name },
});

export const filterChecklist =
  (payload: IChecklistFilter) =>
  ({ dispatch }: IDeps) => {
    dispatch({ type: ChecklistAnalyticsActionTypes.FILTER_CHECKLIST_ANALYTICS, payload });
    return {
      type: ChecklistActionTypes.FILTER_CHECKLIST,
      payload,
    };
  };

export const onEditTaskAssignee =
  (task: IGroup, taskName: string) =>
  ({ dispatch }: IDeps) => {
    const { id: taskId } = task;

    dispatch({
      type: ChecklistAnalyticsActionTypes.ON_EDIT_TASK_ASSIGNEE_ANALYTICS,
      payload: { task, taskId, taskName },
    });

    return {
      type: ChecklistActionTypes.ON_EDIT_TASK_ASSIGNEE,
    };
  };

export const saveTaskAssignee =
  ({ task, name, taskName }: { task: IGroup; name: string; taskName: string }) =>
  ({ dispatch }: IDeps) => {
    const { id } = task;

    dispatch({
      type: ChecklistAnalyticsActionTypes.SAVE_TASK_ASSIGNEE_ANALYTICS,
      payload: { task, taskId: id, taskName, newAssigneeName: name },
    });

    return {
      type: ChecklistActionTypes.SAVE_TASK_ASSIGNEE,
      payload: { taskId: id, assignee: name },
    };
  };

export const onTriggerSocialShareTask =
  (network: string) =>
  ({ getState, dispatch }: IDeps) => {
    const {
      checklist: { tasks, detailsTaskId },
    } = getState();
    const task = detailsTaskId ? tasks[detailsTaskId] : undefined;

    if (task && task.name) {
      dispatch({
        type: ChecklistAnalyticsActionTypes.ON_TRIGGER_SOCIAL_SHARE_TASK_ANALYTICS,
        payload: { task, taskName: task.name, network },
      });
    }

    return {
      type: ChecklistActionTypes.ON_TRIGGER_SOCIAL_SHARE_TASK,
    };
  };

export const onCopyTaskUrlAnalytics =
  (method: string) =>
  ({ getState, dispatch }: IDeps) => {
    const {
      checklist: { tasks, detailsTaskId },
      app: { pathname, device: host },
    } = getState();
    const task = detailsTaskId ? tasks[detailsTaskId] : undefined;
    if (task && task.name) {
      const url = pathname ? `${host}${pathname}` : '';
      dispatch({
        type: ChecklistAnalyticsActionTypes.ON_COPY_TASK_URL_ANALYTICS,
        payload: { task, taskName: task.name, method, url },
      });
    }

    return {
      type: ChecklistActionTypes.ON_COPY_TASK_URL,
    };
  };

export const followedSubtasksLink =
  (task: TTaskSerialized, taskName: string, url: string) =>
  ({ dispatch }: IDeps) => {
    dispatch({
      type: ChecklistAnalyticsActionTypes.FOLLOWED_SUBTASKS_LINK_ANALYTICS,
      payload: { task, taskName, url },
    });

    return {
      type: ChecklistActionTypes.FOLLOWED_SUBTASKS_LINK,
    };
  };

export const usedTabNavigationOnSubtaskViewAnalytics = (
  task: IGroup,
  taskName: string,
  tab: any,
) => ({
  type: ChecklistAnalyticsActionTypes.USED_TAB_NAVIGATION_ON_SUBTASK_VIEW_ANALYTICS,
  payload: {
    task,
    taskName,
    tab,
  },
});

export const addChecklistTaskToggle =
  (value?: boolean) =>
  ({ getState }: IDeps) => {
    const toggle = typeof value === 'boolean' ? value : !getState().checklist.showAddTask;

    return {
      type: ChecklistActionTypes.ADD_CHECKLIST_TASK_TOGGLE,
      payload: toggle,
    };
  };

export const assignTaskToggle =
  (value?: boolean) =>
  ({ getState }: IDeps) => {
    const toggle = typeof value === 'boolean' ? value : !getState().checklist.showAssignTask;

    return {
      type: ChecklistActionTypes.ASSIGN_TASK_TOGGLE,
      payload: toggle,
    };
  };

export const clickedAddMainTaskAnalytics = () => ({
  type: ChecklistAnalyticsActionTypes.CLICKED_ADD_MAIN_TASK_ANALYTICS,
});

// Moves a main task, with its subtasks, to another period.
export const changeTaskPeriod = (payload: { groupId: string; period: string }) => ({
  type: ChecklistActionTypes.TASKS_PERIOD_CHANGE as const,
  payload,
});

export const changeTaskPeriodSuccess = () => ({
  type: ChecklistActionTypes.TASKS_PERIOD_CHANGE_SUCCESS,
});
